import { AxiosResponse } from "axios";
import { parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import PlannerRepository from "@/repositories/PlannerRepository";
import { LastSeenProcessResult, Process, TagGroupedProcessResponse } from "@/types/Process";
import { apiClient } from "./clients";

const parseUtcDate = (dateText: string): Date => utcToZonedTime(parseISO(dateText), "UTC");

const mapProcess = (process: Process<string>) => ({
  ...process,
  start_time: parseUtcDate(process.start_time),
  end_time: parseUtcDate(process.end_time),
});

const loadTagGroupedProcesses = (customerName: string, siteId: string) =>
  apiClient
    .get<TagGroupedProcessResponse>(`/ops-process/group-tag/${customerName}/${siteId}`)
    .then((response) => {
      const mappedProcesses = response.data.data.map((process) => ({
        ...process,
        processes: process.processes.map((process) => {
          const mappedProcess = {
            ...process,
            start_time: parseUtcDate(process.start_time as unknown as string),
            end_time: parseUtcDate(process.end_time as unknown as string),
            work_intervals: (process.work_intervals || []).map((workInterval) => ({
              ...workInterval,
              start_time: parseUtcDate(workInterval.start_time as unknown as string),
              end_time: parseUtcDate(workInterval.end_time as unknown as string),
            })),
          };
          return {
            ...mappedProcess,
            breaks: PlannerRepository.calculateProcessBreaks(mappedProcess),
          };
        }),
      }));

      return {
        levelOrders: response.data.levelOrders,
        data: mappedProcesses,
      };
    });

const loadProcessVideoUrl = (
  customerName: string,
  siteId: string,
  processDbId: string,
): Promise<{ url: string }> =>
  apiClient
    .get<void, AxiosResponse<{ url: string }>>(
      `/ops-process/process-video/${customerName}/${siteId}/${processDbId}`,
    )
    .then((response) => response.data);

const loadProcessThumbnail = (customerName: string, siteId: string, processDbId: string) =>
  apiClient
    .get<void, AxiosResponse<Blob>>(
      `/ops-process/process-thumbnail/${customerName}/${siteId}/${processDbId}`,
      {
        responseType: "blob",
        headers: {
          "Content-Type": "image/jpeg",
        },
      },
    )
    .then((response) => response.data);

const loadLastSeenProcess = (
  customerName: string,
  siteId: string,
): Promise<LastSeenProcessResult> =>
  apiClient
    .get<void, AxiosResponse<LastSeenProcessResult<string>>>(
      `/ops-process/last-seen-process/${customerName}/${siteId}`,
    )
    .then((response) => {
      return {
        ...response.data,
        process: response.data.process && mapProcess(response.data.process),
      };
    });

const loadNextLastSeenProcess = (
  customerName: string,
  siteId: string,
  processId: string,
): Promise<LastSeenProcessResult> =>
  apiClient
    .get<void, AxiosResponse<LastSeenProcessResult<string>>>(
      `/ops-process/last-seen-process/${customerName}/${siteId}/${processId}/next`,
    )
    .then((response) => {
      return {
        ...response.data,
        process: response.data.process && mapProcess(response.data.process),
      };
    });

export default {
  loadTagGroupedProcesses,
  loadProcessVideoUrl,
  loadProcessThumbnail,
  loadLastSeenProcess,
  loadNextLastSeenProcess,
};
